import { useEffect, useRef } from 'react';
import { Rect, Transformer, Text, Circle, Group, Image } from 'react-konva';
import dropdownData from '../../../utils/dropdownData.json'; // Adjust the path to your JSON file
import { getTextColor, rotatePoint } from '../annotationUtils';
import useImage from 'use-image';
import Konva from 'konva';
// import { Annotation as AnnotationType } from "./types/annotation"

const getCloseButtonPosition = (node: any, event: any) => {
  const scaleX = node.scaleX(); // Current horizontal scale

  // Reset the scaling after transform
  node.scaleX(1);
  node.scaleY(1);

  // Calculate the center of the rectangle
  const wi = Math.max(5, node.width() * scaleX);
  const rotation = event.target.rotation() ? event.target.rotation() : 0;
  const x = event.target.x();
  const y = event.target.y();

  // Get rectangle's corners before rotation
  const corners = [
    // { x: x, y: y }, // Top-left
    { x: x + wi, y: y }, // Top-right
    // { x: x + width, y: y + height }, // Bottom-right
    // { x: x, y: y + height }, // Bottom-left
  ];

  // Rotate each corner
  const rotatedCorners = corners.map(corner => rotatePoint(corner.x, corner.y, x, y, rotation));

  // Normalize corners (scale relative to canvas size)
  const normalizedCorners = rotatedCorners.map(({ x, y }) => ({
    x: x.toFixed(6),
    y: y.toFixed(6),
  }));

  const obbString = `${normalizedCorners.map(({ x, y }) => `${x} ${y}`).join(' ')}`;
  return obbString;
};
const REFERENCE_CANVAS_WIDTH = 1000;
const MIN_SIZE_SCALE = 0.6;
const MAX_SIZE_SCALE = 2;

export const Annotation = (props: any) => {
  const { shapeProps, isSelected, isEditable = true, hoverCursor, user, removeAnnotation, onSelect, onChange } = props;
  // Size stroke/label/anchors relative to the canvas so they scale with the image.
  const canvasWidth = Number(shapeProps?.frameWidth) > 0 ? Number(shapeProps.frameWidth) : REFERENCE_CANVAS_WIDTH;
  const sizeScale = Math.min(MAX_SIZE_SCALE, Math.max(MIN_SIZE_SCALE, canvasWidth / REFERENCE_CANVAS_WIDTH));
  const strokeWidth = 2 * sizeScale;
  const fontSize = 16 * sizeScale;
  const labelOffsetY = 20 * sizeScale;
  const labelPaddingX = 10 * sizeScale;
  const labelHeight = 20 * sizeScale;
  const anchorSize = Math.max(6, 10 * sizeScale);
  const shapeRef = useRef<Konva.Transformer | null>(null);
  const transformRef = useRef<Konva.Transformer | null>(null);
  const [approvedImage] = useImage('/img/approved.png'); // Load custom icon
  const [pendingImage] = useImage('/img/pending.png'); // Load custom icon

  useEffect(() => {
    if (isSelected && transformRef.current) {
      console.log('Annotation selected.');
      // attaching transformer manually on selection of an annotation
      transformRef.current.setNode(shapeRef.current);
      transformRef.current.getLayer().batchDraw();
      // setSelectedDamageType('Bent');
    }
  }, [isSelected]);

  const onMouseEnter = (event: any) => {
    // Keep read-only annotation mode visually non-interactive.
    event.target.getStage().container().style.cursor = isEditable ? 'move' : (hoverCursor ?? 'default');
  };

  const onMouseLeave = (event: any) => {
    event.target.getStage().container().style.cursor = isEditable ? 'crosshair' : (hoverCursor ?? 'default');
  };
  const demo = (val: string) => {
    const dd = dropdownData.damages.find((ddd: any) => ddd.code === val);
    if (dd) return dd.color;
    else return '#b51616';
  };

  return (
    <>
      <Group>
        <Rect
          fill="transparent"
          // stroke="#b51616"
          stroke={shapeProps?.review_status === 'PENDING' ? demo('00') : demo(shapeProps?.type.substring(2, 4))}
          strokeWidth={strokeWidth}
          onMouseDown={(e: Konva.KonvaEventObject<MouseEvent>) => {
            if (!isEditable) {
              return;
            }
            e.cancelBubble = true;
            onSelect();
          }}
          ref={shapeRef}
          {...shapeProps}
          // rotation={true}
          // draggable={
          //   (shapeProps.created_by === user?.USER_ID &&
          //     shapeProps.review_status === "PENDING") ||
          //   user?.ROLE_ID === 6
          //     ? true
          //     : false
          // }
          draggable={isEditable}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onDragEnd={(event: any) => {
            if (!isEditable) {
              return;
            }
            const node = shapeRef.current;
            const clsButtonPos = getCloseButtonPosition(node, event);
            onChange({
              ...shapeProps,
              rotation: event.target.rotation() ? event.target.rotation() : 0,
              p: event.target.position(),
              x: event.target.x(),
              y: event.target.y(),
              x1: event.target.x(),
              y1: event.target.y(),
              x2: event.target.x() + Math.max(5, node.width() * 1),
              y2: event.target.y() + Math.max(5, node.height() * 1),
              xx2: clsButtonPos.split(' ')[0],
              yy2: clsButtonPos.split(' ')[1],
              action: 'MODIFIED',
              type: shapeProps?.type.substring(0, 5) + '-M',
              review_status: shapeProps.review_status,
              frameWidth: shapeProps.frameWidth,
              frameHeight: shapeProps.frameHeight,
              imageWidth: shapeProps.imageWidth,
              imageHeight: shapeProps.imageHeight,
              coordinateSpace: 'canvas',
              // width: Math.max(5, node.width() * 1),
              // height: Math.max(node.height() * 1),
            });
          }}
          onTransformEnd={(event: any) => {
            if (!isEditable) {
              return;
            }
            const node = shapeRef.current;
            const scaleX = node.scaleX(); // Current horizontal scale
            const scaleY = node.scaleY(); // Current vertical scale

            // Reset the scaling after transform
            node.scaleX(1);
            node.scaleY(1);
            const clsButtonPos = getCloseButtonPosition(node, event);

            onChange({
              ...shapeProps,
              rotation: event.target.rotation() ? event.target.rotation() : 0,
              p: event.target.position(),
              x: event.target.x(),
              y: event.target.y(),
              x1: event.target.x(),
              y1: event.target.y(),
              x2: event.target.x() + Math.max(5, node.width() * scaleX),
              y2: event.target.y() + Math.max(5, node.height() * scaleY),
              xx2: clsButtonPos.split(' ')[0],
              yy2: clsButtonPos.split(' ')[1],
              width: Math.max(5, node.width() * scaleX), // Save updated width
              height: Math.max(5, node.height() * scaleY), // Save updated height
              action: 'MODIFIED',
              type: shapeProps?.type.substring(0, 5) + '-M',
              review_status: shapeProps.review_status,
              frameWidth: shapeProps.frameWidth,
              frameHeight: shapeProps.frameHeight,
              imageWidth: shapeProps.imageWidth,
              imageHeight: shapeProps.imageHeight,
              coordinateSpace: 'canvas',

              // width: Math.max(5, node.width() * 1),
              // height: Math.max(node.height() * 1),
            });
          }}
        />
        {/* <Rect text="This is Konva" x={200} y={300} fontSize={20} fill="black" /> */}
        {false && isSelected && (
          <>
            <Circle x={shapeProps.xx2} y={shapeProps.yy2 - 20} radius={8} fill="red" />
            <Text
              x={shapeProps.xx2 - 9} // Adjust position for the close icon
              y={shapeProps.yy2 - 27} // Adjust position for the close icon
              text=" X"
              fontSize={15}
              fill="white"
              fontStyle="bold"
              onClick={() => removeAnnotation(shapeProps.id)}
              style={{ cursor: 'pointer' }}
            />
            {true && user.ROLE_ID === 5 && (
              <Image
                image={shapeProps.review_status === 'APPROVED' ? approvedImage : pendingImage}
                x={shapeProps.x - 20 + shapeProps.width / 2}
                y={shapeProps.y2 + 50} // Position above shape
                width={40}
                height={40}
                offsetX={10}
                offsetY={10}
                onMouseDown={(e: any) => {
                  // e.cancelBubble = true;
                  // console.log(e.target.rotation()?e.target.rotation():0);
                  console.log(shapeProps);
                  // shapeProps.rotation()
                  // const angle = 180 + 10; // Rotate shape
                  // shapeProps.rotation(angle);
                  // shapeProps.getLayer().batchDraw();
                }}
                onDragEnd={(event: any) => {
                  onChange({
                    ...shapeProps,
                    rotation: event.target.rotation() ? event.target.rotation() : 0,
                  });
                }}
                onClick={() => {
                  onChange({
                    ...shapeProps,
                    review_status:
                      shapeProps.review_status === 'PENDING'
                        ? 'APPROVED'
                        : shapeProps.review_status === 'APPROVED'
                          ? 'PENDING'
                          : 'APPROVED',
                    action: 'APPROVED',
                  });
                }}
              />
            )}
          </>
        )}
        {Number.isFinite(shapeProps?.x) && (
          <>
            <Rect
              x={shapeProps.x}
              y={shapeProps.y - labelOffsetY}
              width={fontSize * (String(shapeProps.type ?? '').length * 0.62 + 1.2)}
              height={labelHeight}
              fill={shapeProps?.review_status === 'PENDING' ? demo('00') : demo(shapeProps?.type.substring(2, 4))}
            />
            <Text
              x={shapeProps.x + labelPaddingX}
              y={shapeProps.y - labelOffsetY + 3 * sizeScale}
              text={shapeProps.type}
              fontSize={fontSize}
              fontFamily="Arial"
              fontStyle="bold"
              fill={
                shapeProps?.review_status === 'PENDING' ? getTextColor(demo('00')) : getTextColor(demo(shapeProps?.type.substring(2, 4)))
              }
              align="center"
            />
          </>
        )}

        {isSelected && isEditable && (
          <>
            <Transformer
              rotateEnabled={true}
              flipEnabled={false}
              ref={transformRef}
              stroke="#45678a"
              // enabledAnchors={["top-left", "top-right", "bottom-left", "bottom-right"]}
              anchorSize={anchorSize}
              borderStrokeWidth={Math.max(1, sizeScale)}
            />
          </>
        )}
      </Group>
    </>
  );
};
